import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Logo from "./Logo";
import useApiProfile from "../../hooks/useApiProfile";
import { requestPasswordGate } from "./PasswordGateway";
import "../../styles/layout/_navbar.scss";

const LAST_ACTIVITY_KEY = "moozLastActivityAt";
const TIMEOUT_RETURN_TO_KEY = "moozTimeoutReturnTo";

const NAV_LINKS = [
    { path: "/about", label: "About" },
    { path: "/faqs", label: "FAQs" },
    { path: "/pricing", label: "Pricing" },
];

const UTILITY_LINKS = [
    { path: "/about", label: "About us" },
    { path: "/faqs", label: "FAQs" },
    { path: "/help", label: "Help centre" },
    { path: "/pricing", label: "Pricing" },
    { path: "/terms-of-service", label: "Terms of service" },
    { path: "/privacy-policy", label: "Privacy policy" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { isAuthenticated, user, logout } = useAuth0();
    const { profile } = useApiProfile();

    const close = useCallback(() => {
        setOpen(false);
        document.body.classList.remove("no-nav-scroll");
    }, []);

    const toggle = useCallback(() => {
        setOpen((prev) => {
            const next = !prev;
            document.body.classList.toggle("no-nav-scroll", next);
            return next;
        });
    }, []);

    const handleLogout = useCallback(() => {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem(TIMEOUT_RETURN_TO_KEY);
        logout({ logoutParams: { returnTo: window.location.origin } });
        close();
    }, [logout, close]);
    const handleGateLogin = useCallback(() => {
        close();
        requestPasswordGate({ type: "auth0Login" });
    }, [close]);

    const handleGateSignup = useCallback(() => {
        close();
        requestPasswordGate({
            type: "auth0Login",
            options: { authorizationParams: { screen_hint: "signup" } },
        });
    }, [close]);

    // Close on route change
    useEffect(() => {
        close();
    }, [location.pathname, close]);

    // Escape key
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") close();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, close]);

    const displayName = useMemo(() => {
        const dbName =
            `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim();
        if (dbName) return dbName;
        if (!user) return "Profile";
        return (
            `${user.given_name ?? ""} ${user.family_name ?? ""}`.trim() ||
            user.name ||
            "Profile"
        );
    }, [profile?.first_name, profile?.last_name, user]);

    const initials = displayName
        ? displayName
              .split(" ")
              .map((w) => w[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()
        : "P";

    return (
        <nav className="navbar" aria-label="Main navigation">
            <div className="navbar__inner">
                {/* Logo */}
                <span className="navbar__logo" onClick={close}>
                    <Logo />
                </span>

                {/* Desktop links — unauthenticated only */}
                {!isAuthenticated && (
                    <div className="navbar__links">
                        {NAV_LINKS.map(({ path, label }) => (
                            <Link
                                key={path}
                                to={path}
                                className={`navbar__link${location.pathname === path ? " navbar__link--active" : ""}`}
                            >
                                {label}
                            </Link>
                        ))}
                    </div>
                )}

                {/* Desktop auth */}
                <div className="navbar__auth">
                    {isAuthenticated ? (
                        <>
                            <Link to="/profile" className="navbar__profile-btn">
                                <span
                                    className="navbar__avatar"
                                    aria-hidden="true"
                                >
                                    {initials}
                                </span>
                                <span className="navbar__profile-name">
                                    {displayName}
                                </span>
                            </Link>
                            <Link to="/dashboard" className="navbar__cta">
                                Dashboard
                            </Link>
                        </>
                    ) : (
                        <>
                            <button
                                className="navbar__btn-login"
                                onClick={handleGateLogin}
                            >
                                Log in
                            </button>
                            <button
                                className="navbar__cta"
                                onClick={handleGateSignup}
                            >
                                Get started
                            </button>
                        </>
                    )}
                </div>

                {/* Hamburger */}
                <button
                    type="button"
                    className={`navbar__burger${open ? " navbar__burger--open" : ""}`}
                    onClick={toggle}
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    aria-controls="nav-menu"
                >
                    <span className="navbar__burger-bar" />
                    <span className="navbar__burger-bar" />
                </button>
            </div>

            {/* ── Mobile drawer (slide from right, keeps original DNA) ── */}
            <div
                id="nav-menu"
                className={`nav-drawer${open ? " nav-drawer--open" : ""}`}
                aria-hidden={!open}
            >
                <div
                    className="nav-drawer__backdrop"
                    onClick={close}
                    aria-hidden="true"
                />

                <div
                    className="nav-drawer__panel"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation menu"
                >
                    {/* Close button */}
                    <button
                        type="button"
                        className="nav-drawer__close"
                        onClick={close}
                        aria-label="Close menu"
                    >
                        <span
                            className="nav-drawer__close-icon"
                            aria-hidden="true"
                        />
                    </button>

                    {/* Primary nav — keeps the large type DNA of the original */}
                    <nav
                        className="nav-drawer__primary"
                        aria-label="Mobile navigation"
                    >
                        {isAuthenticated ? (
                            <>
                                <Link
                                    to="/profile"
                                    className="nav-drawer__main-link"
                                    onClick={close}
                                >
                                    <span className="nav-drawer__profile-meta">
                                        <span
                                            className="nav-drawer__avatar"
                                            aria-hidden="true"
                                        >
                                            {initials}
                                        </span>
                                        <span className="nav-drawer__profile-name">
                                            {displayName}
                                        </span>
                                    </span>
                                    <span
                                        className="nav-drawer__arrow"
                                        aria-hidden="true"
                                    />
                                </Link>
                                <Link
                                    to="/dashboard"
                                    className="nav-drawer__main-link"
                                    onClick={close}
                                >
                                    Dashboard
                                    <span
                                        className="nav-drawer__arrow"
                                        aria-hidden="true"
                                    />
                                </Link>
                            </>
                        ) : (
                            NAV_LINKS.map(({ path, label }) => (
                                <Link
                                    key={path}
                                    to={path}
                                    className="nav-drawer__main-link"
                                    onClick={close}
                                >
                                    {label}
                                    <span
                                        className="nav-drawer__arrow"
                                        aria-hidden="true"
                                    />
                                </Link>
                            ))
                        )}
                    </nav>

                    {/* Auth CTA */}
                    <div className="nav-drawer__auth">
                        {isAuthenticated ? (
                            <button
                                className="nav-drawer__logout"
                                onClick={handleLogout}
                            >
                                Log out
                            </button>
                        ) : (
                            <button
                                className="nav-drawer__login-btn"
                                onClick={handleGateLogin}
                            >
                                Log in or create account
                            </button>
                        )}
                    </div>

                    {/* Utility links — keeps the "Useful links" section DNA */}
                    <div
                        className="nav-drawer__utility"
                        aria-label="Useful links"
                    >
                        <p className="nav-drawer__utility-heading">
                            Useful links
                        </p>
                        <div className="nav-drawer__utility-grid">
                            {UTILITY_LINKS.map(({ path, label }) => (
                                <Link
                                    key={path}
                                    to={path}
                                    className="nav-drawer__utility-link"
                                    onClick={close}
                                >
                                    {label}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
}
